import { z } from 'zod'
import { CheckReport } from './checkReport.js'
import { Definitie } from './definitie.js'
import { Block, CompartimentId, DefinitieId, PublicatieId, PublicatieaanbodId, RondeSleutel, UitrolverzoekId, UitvraagRef } from './id.js'
import { Moment, Person } from './moment.js'
import { Sha256 } from './document.js'

/**
 * Een publicatie is een definitie die op slot is en draait (ontwerp 4.1, 4.2).
 *
 * Dit is de enige plek waar de designtimehelft en de runtimehelft elkaar raken:
 * een instantie verwijst naar de publicatie waaronder hij draait (R-100, D-30)
 * en al zijn revisies delen die ene vorm (R-186). Niets in de runtime wijzigt
 * ooit de definitie, en niets in het publiceren raakt ooit lopende gegevens; kom
 * je iets tegen dat beide muteert, dan is dat een ontwerpfout.
 *
 * De definitie zit hier als geheel in en niet als verwijzing. Dat is R-99: bij
 * publicatie gaat de vorm op slot en is daarna niet meer te wijzigen, zonder
 * uitzondering (T7). Een verwijzing naar een concept dat doorloopt zou die
 * eigenschap juist opheffen.
 */
export const Publicatie = z
  .strictObject({
    id: PublicatieId,
    /** Het concept waaruit deze publicatie is bevroren; alleen voor herleidbaarheid (D-45). */
    definitieId: DefinitieId,
    /**
     * Het bouwblok en het uitvraagmoment waarvoor deze publicatie geldt.
     *
     * Samen zijn ze uniek: per bouwblok en per uitvraagmoment geldt maar één
     * gepubliceerd formulier, en een tweede publicatie die het eerste vervangt bestaat
     * niet (D-67, ontwerp 8.8). Die uniciteit wordt in de opslag afgedwongen en niet
     * hier, want twee losse publicaties kunnen elkaar in een schema niet zien.
     *
     * `blok` staat ook in `definitie.blok`, net zoals `definitieId` ook in
     * `definitie.id` staat. Dat is met opzet: de uniciteit hangt aan een kolom en niet
     * aan een pad in een JSON-document, en de `superRefine` hieronder bindt de twee aan
     * elkaar zodat ze niet uiteen kunnen lopen.
     */
    blok: Block,
    uitvraagRef: UitvraagRef,
    /** De bevroren vorm zelf. */
    definitie: Definitie,
    /** Build-nummer van de procesconfiguratie waarin deze publicatie hoort (C008). */
    buildNummer: z.string().min(1),
    /**
     * De image-digest van de pod die deze publicatie serveert (R-101, B2).
     *
     * Een digest en nooit een tag: een publicatie is een container met het
     * formulier en de code die het uitvoert erin, en een tag kan later naar iets
     * anders wijzen. Dan zou een lopend dossier stil van renderer of evaluator
     * wisselen, en dat is precies wat R-101 uitsluit.
     */
    imageDigest: z.string().regex(/^sha256:[0-9a-f]{64}$/, 'verwacht een image-digest, geen tag'),
    /** De versie van de veldcatalogus en van de evaluator die in deze pod zitten. */
    catalogusVersie: z.string().min(1),
    engineVersie: z.string().min(1),
    gepubliceerdOp: Moment,
    gepubliceerdDoor: Person,
    /**
     * Het bewijs dat de publicatiecontroles zijn gelopen (D-46, ontwerp 8.8 stap 1).
     *
     * Hij hangt aan de publicatie en niet aan het concept, want hij gaat over de stand
     * die bevriest en niet over de stand waaraan nog wordt gewerkt. Zo blijft ook jaren
     * later na te gaan waarop een lopend dossier is goedgekeurd, en door wie.
     */
    controleverslag: CheckReport,
    /**
     * Waar de publicatie-pod draait, of niets voor een publicatie van vóór M8.4 (additief
     * binnen `/v1`, ontwerp 3.5).
     *
     * **Waarom hier en niet in een aparte doellijst.** M8-opknipvoorstel open vraag (a):
     * `scripts/publiceer.ts` (en `apps/configurator/src/publiceren.ts`'s `Builder`) kent de
     * hostnaam al vóór de publicatierij wordt weggeschreven, dus dit veld kan in dezelfde stap
     * meekomen. Het alternatief, een doellijst per omgeving bij de koppelvlakdienst
     * (`apps/inlog/manifest.yaml`'s `INLOG_TOEGESTANE_DOELEN`, maar in omgekeerde richting), is
     * op 8 september 2026 bewust niet gekozen: die lijst is al één keer stil achtergebleven bij
     * drie publicaties (`docs/dekking.md`), en `Publicatie` is al de enige bron van waarheid
     * over wat er bij een publicatie hoort.
     *
     * **Https en niet zomaar een URL.** Een link die het processysteem straks aan een gebruiker
     * toont zonder tls zou een sessie in platte tekst versturen; er is geen scenario in dit
     * ontwerp waarin een publicatie-pod zonder tls draait.
     */
    adres: z.url({ protocol: /^https$/ }).optional(),
  })
  .superRefine((publicatie, ctx) => {
    if (publicatie.definitie.id !== publicatie.definitieId) {
      ctx.addIssue({
        code: 'custom',
        path: ['definitieId'],
        message: `definitieId ${publicatie.definitieId} hoort niet bij de bevroren definitie ${publicatie.definitie.id}`,
      })
    }
    if (publicatie.controleverslag.definitieId !== publicatie.definitieId) {
      ctx.addIssue({
        code: 'custom',
        path: ['controleverslag', 'definitieId'],
        // Een verslag over een ander formulier bewijst niets over dit formulier.
        message: `het controleverslag gaat over ${publicatie.controleverslag.definitieId} en deze publicatie over ${publicatie.definitieId}`,
      })
    }
    if (publicatie.definitie.blok !== publicatie.blok) {
      ctx.addIssue({
        code: 'custom',
        path: ['blok'],
        // Zou dit uiteen kunnen lopen, dan bewaakt de unieke index in de opslag een
        // ander bouwblok dan het formulier zelf noemt, en dan is D-67 niets waard.
        message: `blok ${publicatie.blok} hoort niet bij de bevroren definitie van blok ${publicatie.definitie.blok}`,
      })
    }
  })

export type Publicatie = z.infer<typeof Publicatie>

/**
 * Welke compartimenten het blok van een publicatie heeft, en welke daarvan een volgend bouwblok
 * mag tonen (D-69, D-70), uit het contract op het moment van publiceren.
 *
 * Het contract van een blok in een gepubliceerde ronde wijzigt niet (D-43), dus dit bevriezen is
 * geen kopie die kan verlopen. De opslagdienst heeft de contractregistry niet, en heeft dit nodig
 * voor `geeftDoor` in het bericht aan het processysteem (`koppelvlak.md` hoofdstuk 3).
 *
 * **Niet op `Publicatie` zelf.** `Publicatie` is een `strictObject`, en de koppelvlakdienst en de
 * configurator lezen haar strikt; een dienst die ouder is dan de opslagdienst zou een nieuwe sleutel
 * weigeren. Dit gaat daarom mee in het publicatieverzoek en staat in een eigen kolom.
 */
export const PublishedCompartiment = z.strictObject({ id: CompartimentId, geeftDoor: z.boolean() })
export type PublishedCompartiment = z.infer<typeof PublishedCompartiment>

/**
 * De sleutel van de pod van een publicatie, zoals de opslagdienst hem bewaart: alleen de hash (bevinding
 * 1 van de beveiligingsronde van 1 november 2026, ontwerp 9.1).
 *
 * Elke publicatie heeft een eigen sleutel, en de opslagdienst bindt wie hem aanbiedt aan die
 * publicatie: de pod raakt daarmee alleen haar instanties. De sleutel zelf is 32 willekeurige bytes,
 * en `sleutelHash` is de SHA-256 van die sleutel als UTF-8. De configurator zet hem
 * (`PUT /v1/publicaties/{id}/podsleutel`); een tweede keer zetten vervangt hem.
 *
 * **Niet op `Publicatie` zelf**, om dezelfde reden als `PublishedCompartiment`, en omdat een sleutel
 * kan veranderen terwijl een publicatie dat nooit doet (R-99).
 */
export const Podsleutel = z.strictObject({
  publicatieId: PublicatieId,
  sleutelHash: Sha256,
  gezetOp: Moment,
})
export type Podsleutel = z.infer<typeof Podsleutel>

/**
 * Of de pod van een publicatie draait, zoals de opslagdienst het weet (migratie 0045, 1 november 2026).
 *
 * `onbekend` is de stand van elke publicatie van vóór die migratie, en van een nieuwe publicatie tot de
 * uitrol haar pod heeft zien antwoorden: de opslag weet dan niets, en zegt dat ook. `draait` zet
 * `pnpm publiceer` nadat de pod op `/gezond` antwoordt, `opgeruimd` zet `pnpm opruimen` nadat het
 * manifest uit gitops is. `pnpm podstand` vergelijkt de opslag met gitops en zet het verschil recht.
 */
export const PodStateValue = z.enum(['onbekend', 'draait', 'opgeruimd'])
export type PodStateValue = z.infer<typeof PodStateValue>

/**
 * De stand van de pod van een publicatie: de huidige waarde, en wie haar wanneer zette.
 *
 * **Niet op `Publicatie` zelf**, om dezelfde twee redenen als `Podsleutel`: bevroren pods en de
 * koppelvlakdienst lezen `Publicatie` strikt en zouden een nieuwe sleutel weigeren, en een publicatie
 * verandert nooit (R-99) terwijl haar pod wordt uitgerold en opgeruimd. Een eigen tabel, een eigen
 * route (`PUT /v1/publicaties/{id}/podstand`, `GET /v1/podstanden`).
 *
 * Een pod die opnieuw wordt uitgerold na het opruimen (een `git revert` in gitops) gaat terug naar
 * `draait`; de vorige keer staat dan in de wijzigingen (`PodStateChange`).
 */
export const PodState = z.strictObject({
  publicatieId: PublicatieId,
  stand: PodStateValue,
  uitgeroldOp: Moment.optional(),
  uitgeroldDoor: Person.optional(),
  opgeruimdOp: Moment.optional(),
  opgeruimdDoor: Person.optional(),
  gewijzigdOp: Moment,
})
export type PodState = z.infer<typeof PodState>

/**
 * Eén wijziging van de stand van een pod: wie, wanneer, naar welke stand. Alleen toevoegen; de opslag
 * weigert een wijziging of verwijdering van een regel (migratie 0045). Een herhaling van dezelfde stand
 * is geen wijziging en schrijft geen regel.
 */
export const PodStateChange = z.strictObject({
  publicatieId: PublicatieId,
  stand: PodStateValue,
  door: Person,
  op: Moment,
})
export type PodStateChange = z.infer<typeof PodStateChange>

/**
 * Eén regel van `GET /v1/podstanden`: de stand met genoeg van de publicatie om haar pod te vinden.
 * `gewijzigdOp` ontbreekt bij een publicatie waarvoor nog nooit een stand is gezet; die heet `onbekend`.
 */
export const PodStateEntry = PodState.extend({
  blok: Block,
  uitvraagRef: UitvraagRef,
  adres: z.url({ protocol: /^https$/ }).optional(),
  gepubliceerdOp: Moment,
  gewijzigdOp: Moment.optional(),
})
export type PodStateEntry = z.infer<typeof PodStateEntry>

/**
 * Eén regel van `GET /v1/rondes/:sleutel/uitgegeven`: een publicatie van de ronde met de stand van haar
 * pod en hoeveel er is uitgezet (eisenboek 12.42, ontwerp 8.14, tab Uitgegeven, fase 4 van de domeinlaag).
 *
 * Een eigen vorm en geen sleutel erbij op `PodStateEntry`: die leest `scripts/podstand.ts` strikt, en
 * een configurator van vóór fase 4 leest de podstanden ook zo. `instanties` telt elke instantie van de
 * publicatie, ook de volgende in een lijn na een overdracht (U21, R-196); `dossiers` telt op hoeveel
 * dossiers ze is uitgezet. Alleen aantallen, geen id's: de configurator leest geen instanties (9.1).
 */
export const UitgegevenEntry = PodStateEntry.extend({
  definitieId: DefinitieId,
  gepubliceerdDoor: Person,
  instanties: z.number().int().min(0),
  dossiers: z.number().int().min(0),
})
export type UitgegevenEntry = z.infer<typeof UitgegevenEntry>

/**
 * De naam van een publicatie-pod, en daarmee zijn hostnaam: `https://<naam>.<domein>` (M8.4, R-101).
 *
 * Kleine letters, cijfers en koppeltekens, beginnend met een letter, 3 tot 41 tekens: een DNS-label met
 * ruimte voor het domein erachter. Dezelfde regel als de publiceerroute en de scripts al toetsten.
 */
export const PodNaam = z.string().regex(/^[a-z][a-z0-9-]{2,40}$/, 'een podnaam is kleine letters, cijfers en koppeltekens, beginnend met een letter')
export type PodNaam = z.infer<typeof PodNaam>

/**
 * Hoe ver een uitrolverzoek is (ontwerp 8.8, besluit van 2 november 2026, migratie 0054).
 *
 * - `wordt-uitgerold`: de poort en het akkoord zijn gehaald en de uitrolpijplijn is gevraagd, of draait.
 * - `uitgerold`: de pod antwoordt op `/gezond` en zijn podstand is `draait`. Eindstand.
 * - `mislukt`: de pijplijn liep vast; `melding` zegt waar. Opnieuw starten zet het terug op `wordt-uitgerold`.
 *
 * Een eigen waarde naast `PodStateValue` en geen uitbreiding daarvan: `GET /v1/podstanden` lezen de
 * configurator en de scripts strikt, en een nieuwe waarde daar laat een oudere lezer vallen.
 */
export const UitrolverzoekStand = z.enum(['wordt-uitgerold', 'uitgerold', 'mislukt'])
export type UitrolverzoekStand = z.infer<typeof UitrolverzoekStand>

/**
 * Een uitrolverzoek: een definitie die de poort en het akkoord van de ronde-eigenaar haalde, en die de
 * uitrolpijplijn bouwt, publiceert en uitrolt (ontwerp 8.8 stap 4, besluit van 2 november 2026).
 *
 * **Waarom dit bestaat.** Een publicatie draagt de digest van haar image (R-101), dus zij kan pas bestaan
 * als het image er is. De configurator op het cluster kan geen image bouwen; de pijplijn wel. Het verzoek
 * is wat er tussen het akkoord en de publicatie ligt: de pijplijn krijgt alleen zijn id mee en leest de
 * rest hier, zodat wie de pijplijn kan starten geen definitie, naam of akkoord kan verzinnen (D-44).
 *
 * `definitiehash` is de hash van de definitie zoals de poort haar bij het akkoord zag. De pijplijn draait
 * de poort opnieuw en weigert als de hash niet meer klopt: het akkoord gold voor die stand, niet voor een
 * stand die er daarna van werd gemaakt.
 *
 * `publicatieId` staat er zodra de pijplijn de publicatie in de opslag zette, en verandert daarna niet.
 */
export const Uitrolverzoek = z.strictObject({
  id: UitrolverzoekId,
  definitieId: DefinitieId,
  blok: Block,
  ronde: RondeSleutel,
  naam: PodNaam,
  uitvraagRef: UitvraagRef,
  akkoordDoor: Person,
  definitiehash: Sha256,
  ingediendOp: Moment,
  stand: UitrolverzoekStand,
  publicatieId: PublicatieId.optional(),
  /** Waarom de pijplijn vastliep, voor de inrichter en 2AT; nooit een geheim of een stacktrace. */
  melding: z.string().min(1).max(500).optional(),
  /**
   * De sha256 van het token waarmee de pijplijn van dit verzoek zich bij de configurator meldt. Het token zelf
   * bewaart niemand; het geldt alleen voor dit verzoek, op `wordt-uitgerold` en tot `tokenVerlooptOp`.
   */
  tokenHash: Sha256,
  tokenVerlooptOp: Moment,
  gewijzigdOp: Moment,
})
export type Uitrolverzoek = z.infer<typeof Uitrolverzoek>

/** Wat de configurator aanbiedt bij het indienen; de opslag vult blok, ronde, id en momenten aan. */
export const UitrolverzoekRequest = z.strictObject({
  definitieId: DefinitieId,
  naam: PodNaam,
  uitvraagRef: UitvraagRef,
  akkoordDoor: Person,
  definitiehash: Sha256,
  tokenHash: Sha256,
  tokenVerlooptOp: Moment,
})
export type UitrolverzoekRequest = z.infer<typeof UitrolverzoekRequest>

/**
 * Een nieuwe stand van een uitrolverzoek, met wie hem zette; `publicatieId` alleen de eerste keer. Een nieuw
 * token (`tokenHash` met `tokenVerlooptOp`) alleen bij `wordt-uitgerold`: opnieuw starten krijgt een eigen token.
 */
export const UitrolverzoekStandRequest = z
  .strictObject({
    stand: UitrolverzoekStand,
    door: Person,
    publicatieId: PublicatieId.optional(),
    melding: z.string().min(1).max(500).optional(),
    tokenHash: Sha256.optional(),
    tokenVerlooptOp: Moment.optional(),
  })
  .refine((request) => (request.tokenHash === undefined) === (request.tokenVerlooptOp === undefined), {
    message: 'tokenHash en tokenVerlooptOp horen samen',
    path: ['tokenHash'],
  })
  .refine((request) => request.tokenHash === undefined || request.stand === 'wordt-uitgerold', {
    message: 'een nieuw token alleen bij wordt-uitgerold',
    path: ['tokenHash'],
  })
export type UitrolverzoekStandRequest = z.infer<typeof UitrolverzoekStandRequest>


/**
 * Hoe ver een publicatieaanbod is (vier ogen op publiceren, besluit van 3 november 2026, eisenboek 12.57,
 * migratie 0056).
 *
 * - `aangeboden`: iemand met Wijzigen bood deze stand ter publicatie aan; ze wacht op het akkoord.
 * - `goedgekeurd`: een tweede persoon met Publicatieakkoord in het penvoerende domein gaf het akkoord. Daarna
 *   volgt de uitrol (`uitrolverzoekId`) of de publicatie (`publicatieId`).
 * - `afgewezen`: wie het akkoord mag geven, wees het af met een reden.
 * - `ingetrokken`: wie het aanbood, trok het in.
 * - `vervallen`: het formulier is na het aanbieden gewijzigd, dus het aanbod gaat niet meer over de stand
 *   die er nu is. Opnieuw aanbieden kan.
 *
 * De laatste vier zijn eindstanden. Een eigen waarde en geen uitbreiding van `UitrolverzoekStand`: die lezen
 * de configurator en de pijplijn strikt, en een uitrolverzoek bestaat pas na het akkoord.
 */
export const PublicatieaanbodStand = z.enum(['aangeboden', 'goedgekeurd', 'afgewezen', 'ingetrokken', 'vervallen'])
export type PublicatieaanbodStand = z.infer<typeof PublicatieaanbodStand>

/**
 * De vaste identiteit waaronder 2AT een formulier ter publicatie aanbiedt met `pnpm publiceer --aanbieden`
 * (besluit van de productowner van 3 november 2026, eisenboek 12.57).
 *
 * **Waarom een eigen identiteit en geen persoon.** In de testfase heeft één mens de rechten, en twee accounts van
 * dezelfde mens zijn geen vier ogen. Wat 2AT doet is dus eerlijk als beheer vastgelegd en niet als een mens: de
 * ingang `beheer` staat naast `entra` en `portaal`, zoals een toekenning met bron beheer (`admin.ts`). `Person`
 * laat die ingang al toe; er hoefde niets bij. De configurator haalt elke persoon uit een Entra-token
 * (`entra:<oid>`), dus een mens handelt nooit onder deze identiteit, en de opslag weigert een akkoord van een
 * `beheer:`-identiteit (`operator-geeft-geen-akkoord`). Het akkoord komt zo altijd van een mens die een andere
 * partij is dan de aanbieder, en de vierogencontrole van de opslag houdt zonder schakelaar.
 */
export const OPERATOR_AANBIEDER = 'beheer:2at-uitrol' as const

/** Of deze persoon een beheeridentiteit is en geen mens: die biedt hoogstens aan, en geeft nooit een akkoord. */
export function isBeheer(person: string): boolean {
  return person.startsWith('beheer:')
}

/** Een reden bij afwijzen of vervallen: voor mensen, nooit een geheim of een stacktrace. */
const Reden = z.string().trim().min(1).max(500)

/**
 * Een publicatieaanbod: een stand van een definitie die iemand met Wijzigen ter publicatie aanbiedt, met de
 * podnaam en het uitvraagmoment erbij, en die een tweede persoon met Publicatieakkoord goedkeurt (D-44, vier
 * ogen, besluit van 3 november 2026, eisenboek 12.57, ontwerp 8.8 stap 2).
 *
 * **Wat bevriest bij het aanbieden.** De hash van de definitie zoals de poort haar zag, de podnaam (die het
 * adres wordt, R-101) en het uitvraagmoment (per bouwblok en uitvraagmoment één formulier, D-67). Die twee
 * laatste zijn niet terug te draaien, dus ze horen bij wat de tweede persoon goedkeurt en niet bij wat hij
 * zelf kiest; klopt er een niet, dan wijst hij het aanbod af.
 *
 * **Wie het aanbood, keurt het niet goed.** De opslag weigert dat (`eigen-aanbod`) en de database nog een keer
 * (een check op de rij), zodat geen route er omheen kan. Een medeschrijver die niet aanbood mag het wel.
 *
 * `afgehandeldDoor` en `afgehandeldOp` zeggen wie het aanbod uit `aangeboden` haalde en wanneer: het akkoord,
 * de afwijzing, de intrekking, of bij `vervallen` wie het formulier wijzigde.
 */
export const Publicatieaanbod = z
  .strictObject({
    id: PublicatieaanbodId,
    definitieId: DefinitieId,
    blok: Block,
    ronde: RondeSleutel,
    naam: PodNaam,
    uitvraagRef: UitvraagRef,
    definitiehash: Sha256,
    aangebodenDoor: Person,
    aangebodenOp: Moment,
    stand: PublicatieaanbodStand,
    afgehandeldDoor: Person.optional(),
    afgehandeldOp: Moment.optional(),
    /** Bij `afgewezen` de reden van wie afwees, bij `vervallen` waarom het verviel. */
    reden: Reden.optional(),
    /** Het uitrolverzoek dat na het akkoord de pijplijn startte (migratie 0054), als die weg is gebruikt. */
    uitrolverzoekId: UitrolverzoekId.optional(),
    /** De publicatie die eruit kwam, als die zonder uitrolverzoek ontstond (`pnpm publiceer --aanbod`). */
    publicatieId: PublicatieId.optional(),
    gewijzigdOp: Moment,
  })
  .superRefine((aanbod, ctx) => {
    const open = aanbod.stand === 'aangeboden'
    if (open !== (aanbod.afgehandeldDoor === undefined)) {
      ctx.addIssue({ code: 'custom', path: ['afgehandeldDoor'], message: 'een aanbod dat niet meer open staat, zegt door wie; een open aanbod niet' })
    }
    if (aanbod.stand === 'goedgekeurd' && aanbod.afgehandeldDoor === aanbod.aangebodenDoor) {
      // Vier ogen: zou dit ooit in de opslag staan, dan is de regel ergens omzeild.
      ctx.addIssue({ code: 'custom', path: ['afgehandeldDoor'], message: 'wie het aanbod deed, geeft niet zelf het akkoord (vier ogen)' })
    }
    if (aanbod.stand === 'afgewezen' && aanbod.reden === undefined) {
      ctx.addIssue({ code: 'custom', path: ['reden'], message: 'een afwijzing heeft een reden' })
    }
    if (aanbod.stand !== 'goedgekeurd' && (aanbod.uitrolverzoekId !== undefined || aanbod.publicatieId !== undefined)) {
      ctx.addIssue({ code: 'custom', path: ['stand'], message: 'alleen een goedgekeurd aanbod leidt tot een uitrol of een publicatie' })
    }
  })
export type Publicatieaanbod = z.infer<typeof Publicatieaanbod>

/** Wat de configurator meegeeft bij het aanbieden, na de poort; de opslag vult blok, ronde, id en momenten aan. */
export const PublicatieaanbodRequest = z.strictObject({
  definitieId: DefinitieId,
  naam: PodNaam,
  uitvraagRef: UitvraagRef,
  definitiehash: Sha256,
  aangebodenDoor: Person,
})
export type PublicatieaanbodRequest = z.infer<typeof PublicatieaanbodRequest>

/**
 * Het akkoord op een aanbod: wie het geeft, en de hash van de stand die de poort bij het akkoord zag. Is die
 * anders dan bij het aanbieden, dan vervalt het aanbod in plaats van dat het akkoord geldt.
 */
export const PublicatieaanbodAkkoordRequest = z.strictObject({ persoon: Person, definitiehash: Sha256 })
export type PublicatieaanbodAkkoordRequest = z.infer<typeof PublicatieaanbodAkkoordRequest>

/** Een aanbod afwijzen, met de reden die de aanbieder leest. */
export const PublicatieaanbodAfwijzingRequest = z.strictObject({ persoon: Person, reden: Reden })
export type PublicatieaanbodAfwijzingRequest = z.infer<typeof PublicatieaanbodAfwijzingRequest>

/** Een aanbod intrekken; alleen wie het aanbood. */
export const PublicatieaanbodIntrekkingRequest = z.strictObject({ persoon: Person })
export type PublicatieaanbodIntrekkingRequest = z.infer<typeof PublicatieaanbodIntrekkingRequest>
